import { useEffect, useState, type ReactNode } from "react";
import { useRecordContext } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { callApi } from "@/dataprovider/misc-apis";
import { formatListDate } from "@/utils/date-format";
import {
  ProjectShowProgressCharts,
  type ProjectFinancialSummaryShape,
} from "./ProjectShowProgressCharts";

type ApiSummary = {
  takhmeen?: {
    totalCommitted?: unknown;
    totalPaid?: unknown;
    totalPending?: unknown;
    pledgeCount?: number;
  };
  inayat?: { totalInayat?: unknown; entryCount?: number };
  karzan?: {
    karzanTotalPrincipal?: unknown;
    karzanTotalRepaid?: unknown;
    karzanTotalOutstanding?: unknown;
    loanCount?: number;
    karzans?: Array<{
      id: string;
      lenderName?: string | null;
      principalAmount?: unknown;
      repaidAmount?: unknown;
      outstanding?: unknown;
    }>;
  };
  payments?: { totalSpent?: unknown; paymentCount?: number };
  checks?: {
    fundingFromGiftsAndCollections?: unknown;
    totalCollected?: unknown;
    totalSpent?: unknown;
    collectedMinusSpent?: unknown;
    estimatedBudget?: unknown;
    karzanOutstanding?: unknown;
    coverageHint?: unknown;
  };
};

const num = (v: unknown): number => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? 0 : n;
  }
  return 0;
};

const normalizeSummary = (raw: ApiSummary | null): ProjectFinancialSummaryShape | null => {
  if (!raw?.takhmeen) return null;
  const t = raw.takhmeen;
  const i = raw.inayat ?? {};
  const k = raw.karzan ?? {};
  const c = raw.checks ?? {};

  const karzans = (k.karzans ?? []).map((row) => ({
    id: row.id,
    lenderName: row.lenderName,
    principalAmount: num(row.principalAmount),
    repaidAmount: num(row.repaidAmount),
    outstanding: num(row.outstanding),
  }));

  const committed = num(t.totalCommitted);
  const paid = num(t.totalPaid);
  const pending = Math.max(0, committed - paid);

  const pay = raw.payments ?? {};
  const totalSpent = num(pay.totalSpent ?? c.totalSpent);
  const funding = num(c.fundingFromGiftsAndCollections);
  const totalCollected = num(c.totalCollected) || funding;

  return {
    takhmeen: {
      pledgeCount: t.pledgeCount,
      totalCommitted: committed,
      totalPaid: paid,
      totalPending: pending,
    },
    inayat: {
      entryCount: i.entryCount,
      totalInayat: num(i.totalInayat),
    },
    payments: {
      paymentCount: pay.paymentCount,
      totalSpent,
    },
    karzan: {
      loanCount: k.loanCount,
      karzanTotalPrincipal: num(k.karzanTotalPrincipal),
      karzanTotalRepaid: num(k.karzanTotalRepaid),
      karzanTotalOutstanding: num(k.karzanTotalOutstanding),
      karzans,
    },
    checks: {
      fundingFromGiftsAndCollections: funding,
      totalCollected,
      totalSpent,
      collectedMinusSpent: totalCollected - totalSpent,
      estimatedBudget: num(c.estimatedBudget),
      karzanOutstanding: num(c.karzanOutstanding),
      coverageHint: num(c.coverageHint),
    },
  };
};

type ProjectRecord = {
  name?: string | null;
  code?: string | null;
  status?: string | null;
  estimatedBudget?: unknown;
  unitAmount?: unknown;
  currency?: string | null;
  estimatedCompletionDate?: string | Date | null;
};

const InlineItem = ({
  label,
  children,
  emphasize,
}: {
  label: string;
  children: ReactNode;
  emphasize?: boolean;
}) => (
  <Box
    sx={{ flexShrink: 0, display: "flex", alignItems: "baseline", gap: 0.5, whiteSpace: "nowrap" }}
  >
    <Typography component="span" variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Box
      component="span"
      sx={{
        ...(emphasize
          ? { fontSize: "1.35rem", fontWeight: 700, lineHeight: 1.2 }
          : { typography: "body2", fontWeight: 600 }),
        color: emphasize ? "primary.main" : "text.primary",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {children}
    </Box>
  </Box>
);

const ProjectIdentityBar = () => {
  const record = useRecordContext<ProjectRecord>();
  if (!record) return null;

  const currencyRaw = String(record.currency ?? "INR").trim() || "INR";
  let currency = currencyRaw;
  try {
    Intl.NumberFormat("en-IN", { style: "currency", currency: currencyRaw }).format(0);
  } catch {
    currency = "INR";
  }
  const money = (value: unknown) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
      maximumSignificantDigits: 12,
    }).format(num(value));

  const status = String(record.status ?? "").toUpperCase();
  const statusColor =
    status === "ACTIVE"
      ? "success"
      : status === "CLOSED" || status === "COMPLETED"
        ? "default"
        : "info";

  const completion =
    record.estimatedCompletionDate != null && record.estimatedCompletionDate !== ""
      ? formatListDate(record.estimatedCompletionDate)
      : "—";

  return (
    <Card variant="outlined" sx={{ minWidth: 0 }}>
      <CardContent
        sx={{
          py: 1.25,
          px: { xs: 1.5, sm: 2 },
          "&:last-child": { pb: 1.25 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.25}
          divider={<Divider orientation="vertical" flexItem sx={{ borderColor: "divider" }} />}
          sx={{
            flexWrap: "nowrap",
            overflowX: "auto",
            minWidth: 0,
            typography: "body2",
            scrollbarWidth: "thin",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{
              flex: "1 1 140px",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title={record.name ?? undefined}
          >
            {record.name ?? "—"}
          </Typography>

          <InlineItem label="Code">
            <Box
              component="span"
              sx={{ fontFamily: "ui-monospace, monospace", fontSize: "0.8125rem" }}
            >
              {record.code?.trim() ? record.code : "—"}
            </Box>
          </InlineItem>

          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography component="span" variant="caption" color="text.secondary">
              Status
            </Typography>
            {status ? (
              <Chip label={status} size="small" color={statusColor} variant="outlined" />
            ) : (
              "—"
            )}
          </Box>

          <InlineItem label="Currency">{currency}</InlineItem>
          <InlineItem label="Budget" emphasize>
            {money(record.estimatedBudget)}
          </InlineItem>
          <InlineItem label="Unit">{money(record.unitAmount)}</InlineItem>
          <InlineItem label="Target">{completion}</InlineItem>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ProjectCashSummaryCard = ({ summary }: { summary: ProjectFinancialSummaryShape }) => {
  const record = useRecordContext<ProjectRecord>();
  const currencyRaw = String(record?.currency ?? "INR").trim() || "INR";
  let currency = currencyRaw;
  try {
    Intl.NumberFormat("en-IN", { style: "currency", currency: currencyRaw }).format(0);
  } catch {
    currency = "INR";
  }
  const money = (value: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
      maximumSignificantDigits: 12,
    }).format(value);

  const budget = summary.checks.estimatedBudget;
  const collected = summary.checks.totalCollected;
  const spent = summary.checks.totalSpent;
  const net = summary.checks.collectedMinusSpent;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.25 }}>
          Budget vs cash flow
        </Typography>
        <Stack
          direction="row"
          alignItems="stretch"
          spacing={1.5}
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ flexWrap: "wrap", rowGap: 1.5 }}
        >
          <Box sx={{ minWidth: 120, flex: "1 1 120px" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Budget
            </Typography>
            <Typography variant="h6" fontWeight={700} color="primary.main">
              {money(budget)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 120, flex: "1 1 120px" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Collected
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {money(collected)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Takhmeen + Inayat
            </Typography>
          </Box>
          <Box sx={{ minWidth: 120, flex: "1 1 120px" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Work spent
            </Typography>
            <Typography variant="h6" fontWeight={700} color="warning.dark">
              {money(spent)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {summary.payments?.paymentCount ?? 0} payment(s)
            </Typography>
          </Box>
          <Box sx={{ minWidth: 120, flex: "1 1 120px" }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Net after spend
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color={net >= 0 ? "success.main" : "error.main"}
            >
              {money(net)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Collected − work payments
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ProjectOverviewBody = () => {
  const record = useRecordContext();
  const projectId = record?.id as string | undefined;
  const [summary, setSummary] = useState<ProjectFinancialSummaryShape | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    callApi({ location: "projects", method: "GET", id: `${projectId}/financial-summary` })
      .then((res) => {
        if (!cancelled) setSummary(normalizeSummary(res.data as ApiSummary));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!projectId) return null;
  if (loading) {
    return (
      <Box
        sx={{
          pt: 2,
          mt: 0.5,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          py: 3,
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (!summary) {
    return (
      <Box sx={{ pt: 2, mt: 0.5, borderTop: 1, borderColor: "divider" }}>
        <Typography color="text.secondary" sx={{ py: 2 }}>
          Financial summary could not be loaded.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minWidth: 0,
        width: "100%",
        pt: 2,
        mt: 0.5,
        borderTop: 1,
        borderColor: "divider",
      }}
    >
      <Typography
        variant="subtitle2"
        fontWeight={700}
        color="text.secondary"
        sx={{ mb: 1.5, letterSpacing: 0.02 }}
      >
        Financial overview
      </Typography>
      <ProjectCashSummaryCard summary={summary} />
      <ProjectShowProgressCharts summary={summary} />
    </Box>
  );
};

export const ProjectShowOverview = () => (
  <Stack spacing={2} sx={{ px: { xs: 0, sm: 0 }, minWidth: 0 }}>
    <ProjectIdentityBar />
    <ProjectOverviewBody />
  </Stack>
);
